import { jsx as _jsx } from "react/jsx-runtime";
/**
 * One surface's material, textures and all, or the magenta placeholder when a texture
 * never draws. Godot binds a material per surface, so texture resolution lives in a
 * component rendered once per surface, which keeps `useResource` one call per component
 * for any surface count.
 */
import { useMemo } from 'react';
import { useThree } from '@react-three/fiber';
import { MISSING_TEXTURE_MATERIAL } from '../../resources/materials/standardmaterial3d/materialBag';
import { drawnBag, materialBagElement, StandardMaterialSlot } from './StandardMaterialSlot';
import { pointDrawOf } from './godotPoint';
import { particleDrawOf } from './godotParticles';
import { useSurfaceMaterial } from './useSurfaceMaterial';
/**
 * One surface's material slot, textures and all, whichever file the material came from.
 * A surface with no usable material draws Godot's default one, which
 * `useSurfaceMaterial` derives from null scalars (ADR-0041). A surface whose texture
 * never draws, a file that cannot load or a ViewportTexture in a pass cycle, draws the
 * magenta placeholder.
 */
export function SurfaceMaterialSlot({ source, attach, points, particles }) {
    const { scalars, bags } = useSurfaceMaterial(source);
    const pixelRatio = useThree((state) => state.viewport.dpr);
    const pointDraw = useMemo(() => (points ? pointDrawOf(scalars, pixelRatio, points) : undefined), [points, scalars, pixelRatio]);
    const particleDraw = useMemo(() => (particles ? { draw: particleDrawOf(scalars), program: particles } : undefined), [particles, scalars]);
    const draws = useMemo(() => ({ pointDraw, particles: particleDraw }), [pointDraw, particleDraw]);
    if (!bags)
        return materialBagElement(drawnBag(MISSING_TEXTURE_MATERIAL, draws), attach, null);
    return _jsx(StandardMaterialSlot, { bags: bags, attach: attach, draws: draws });
}
//# sourceMappingURL=SurfaceMaterialSlot.js.map